/**
 * Task Detail Drawer
 *
 * Slide-in drawer showing full task detail with sticky header (inline-editable
 * status/priority badges, breadcrumb metadata), scrollable content (description,
 * event timeline), and sticky footer (assigned-to, SplitButton actions).
 *
 * Mirrors the IssueDrawerV2 layout pattern.
 *
 * @module features/tasks/components/TaskDetailDrawer
 */

import React from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerCloseButton,
  Text,
  Flex,
  Spinner,
  Markdown,
  TagBadge,
  TagPicker,
  hashStringToTagColor,
  InlineEditableText,
  type BadgeVariant,
  type TagColor,
  type TagOption, badgeVariantToTagColor } from '@chatsdet/ui';
import { At } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  useTask,
  useSubtasks,
  useTaskComments,
  useCreateTaskComment,
  useSetTaskTags,
  useUpdateTask,
} from '../hooks/useTasks';
import { SectionLabel } from '../../cards/components/SectionLabel';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import { useAuth } from '../../../contexts/AuthContext';
import { useCreateTagMutation, useTagsQuery } from '../../../hooks/useTags';
import { StatusEditableBadge } from './StatusEditableBadge';
import { PriorityEditableBadge } from './PriorityEditableBadge';
import { AddCustomerButton } from './AddCustomerButton';
import { CustomerBadge } from './CustomerBadge';
import { CreateSubtaskButton } from './CreateSubtaskButton';
import { TaskDrawerFooter } from './TaskDrawerFooter';
import { CommentsTab } from '../../../ui/components/issues/tabs/CommentsTab';
import type { AgentTask, AgentTaskStatus } from '../types';
const STATUS_BADGE_VARIANTS: Record<AgentTaskStatus, BadgeVariant> = {
  waiting: 'status-waiting',
  actionable: 'status-queued',
  in_progress: 'status-running',
  completed: 'status-success',
  canceled: 'status-failed',
  expired: 'warning',
};
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function subtaskStatusVariant(status: string): BadgeVariant {
  return STATUS_BADGE_VARIANTS[status as AgentTaskStatus] ?? 'default';
}

interface TaskDetailDrawerProps {
  taskId: string;
}

export function TaskDetailDrawer({ taskId }: TaskDetailDrawerProps) {
  const { data, isLoading, refetch } = useTask(taskId);
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const membersQuery = useOrgMembers();
  const members = membersQuery.data?.items ?? [];
  const { user } = useAuth();
  const updateTask = useUpdateTask();
  const [commentText, setCommentText] = React.useState('');
  const [commentPage, setCommentPage] = React.useState(1);
  const commentsQuery = useTaskComments(taskId, commentPage);
  const createComment = useCreateTaskComment();
  const subtasksQuery = useSubtasks(taskId);
  const tagsQuery = useTagsQuery();
  const createTag = useCreateTagMutation();
  const setTaskTags = useSetTaskTags();

  const task = data?.data;
  const allTagOptions = React.useMemo<TagOption[]>(
    () =>
      tagsQuery.data?.data.map((tag) => ({ id: tag.id, name: tag.name, color: tag.color })) ?? [],
    [tagsQuery.data?.data]
  );

  /** Resolve a user ID or actor string to a display name */
  const resolveActorName = (actor: string | null): string => {
    if (!actor) return 'system';
    const member = members.find((m) => m.userId === actor);
    if (member) return member.displayName || member.primaryEmail || actor;
    // If it doesn't look like a UUID, show as-is (e.g. "system")
    if (!actor.includes('-') || actor.length < 32) return actor;
    return actor.slice(0, 8);
  };

  const handleDeleted = () => {
    void navigate({
      to: '/tasks',
      search: { ...search, taskId: undefined },
    });
  };

  if (isLoading) {
    return (
      <Drawer hideCloseButton>
        <div className="flex items-center justify-center h-full">
          <Spinner />
        </div>
      </Drawer>
    );
  }

  if (!task) {
    return (
      <Drawer hideCloseButton>
        <div className="flex items-center justify-center h-full">
          <Text className="text-muted-foreground">Task not found</Text>
        </div>
      </Drawer>
    );
  }

  const handleSuccess = () => {
    void refetch();
  };

  const triggerConfigStr =
    task.triggerConfig && Object.keys(task.triggerConfig).length > 0
      ? Object.entries(task.triggerConfig)
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ')
      : null;

  const canEdit =
    task.status !== 'completed' && task.status !== 'canceled' && task.status !== 'expired';

  // Who submitted the task — "me" when it's the current viewer.
  const submitterName = task.createdBy
    ? task.createdBy === user?.id
      ? 'me'
      : resolveActorName(task.createdBy)
    : null;

  const selectedTagOptions: TagOption[] =
    task.tagDetails && task.tagDetails.length > 0
      ? task.tagDetails.map((tag) => ({ id: tag.id, name: tag.name, color: tag.color }))
      : task.tags.map((tagName) => {
          const existing = allTagOptions.find((tag) => tag.name === tagName);
          return existing ?? { id: tagName, name: tagName, color: hashStringToTagColor(tagName) };
        });

  const handleTagsChange = (nextTags: TagOption[]) => {
    const persistedTagIds = nextTags.map((tag) => tag.id).filter((id) => UUID_REGEX.test(id));
    setTaskTags.mutate({ id: task.id, tagIds: persistedTagIds });
  };

  const handleCreateTag = async (name: string, color: TagColor): Promise<TagOption> => {
    const result = await createTag.mutateAsync({ name, color });
    return { id: result.tag.id, name: result.tag.name, color: result.tag.color };
  };

  return (
    <Drawer hideCloseButton>
      <DrawerHeader className="space-y-3">
        {/* Row 1: Badges + edit + close */}
        <Flex align="center" justify="between" gap="md">
          <Flex align="center" gap="sm" wrap>
            <StatusEditableBadge task={task} onSuccess={handleSuccess} size="sm" />
            <PriorityEditableBadge task={task} onSuccess={handleSuccess} size="sm" />
            {task.assignedTo && (
              <TagBadge
                name={task.assignedTo}
                color="gray"
                size="sm"
                icon={<At size={12} />}
                tooltip={task.assignedTo}
              />
            )}
          </Flex>
          <Flex align="center" gap="xs">
            <DrawerCloseButton />
          </Flex>
        </Flex>

        {/* Title — double-click to edit inline */}
        <InlineEditableText
          value={task.title}
          canEdit={canEdit}
          onSave={(title) => updateTask.mutate({ id: task.id, title })}
          ariaLabel="Task title"
          placeholder="Untitled task"
          className="text-xl font-semibold leading-tight"
          inputClassName="text-xl font-semibold leading-tight"
        />

        {/* Breadcrumbs below title */}
        <Flex align="center" gap="xs" wrap className="min-w-0">
          <Text size="sm" className="text-muted-foreground">
            {task.triggerType}
          </Text>
          {task.sourceCommit && (
            <>
              <Text size="sm" className="text-muted-foreground">
                /
              </Text>
              <Text size="sm" className="text-muted-foreground font-mono">
                {task.sourceCommit.slice(0, 7)}
              </Text>
            </>
          )}
          {triggerConfigStr && (
            <>
              <Text size="sm" className="text-muted-foreground">
                /
              </Text>
              <Text size="sm" className="text-muted-foreground font-mono">
                {triggerConfigStr}
              </Text>
            </>
          )}
          <Text size="sm" className="text-muted-foreground">
            /
          </Text>
          <Text size="sm" className="text-muted-foreground">
            {formatDistanceToNow(new Date(task.createdAt))} ago
          </Text>
          {submitterName && (
            <Text size="sm" className="text-muted-foreground">
              · submitted by <span className="text-foreground">{submitterName}</span>
            </Text>
          )}
          {task.completedAt && (
            <>
              <Text size="sm" className="text-muted-foreground">
                /
              </Text>
              <Text size="sm" className="text-muted-foreground">
                completed {formatDistanceToNow(new Date(task.completedAt))} ago
              </Text>
            </>
          )}
        </Flex>

        {/* Parent link (if subtask) */}
        {task.parentTaskId && (
          <Text size="sm" className="text-muted-foreground">
            Subtask of{' '}
            <button
              className="underline hover:text-foreground font-mono"
              onClick={() =>
                void navigate({
                  to: '/tasks',
                  search: { ...search, taskId: task.parentTaskId },
                })
              }
            >
              {task.parentTaskId.slice(0, 8)}
            </button>
          </Text>
        )}

        {/* Tags */}
        {canEdit ? (
          <TagPicker
            tags={allTagOptions}
            selectedTags={selectedTagOptions}
            onChange={handleTagsChange}
            onCreateTag={handleCreateTag}
            isLoading={tagsQuery.isLoading}
            isDisabled={setTaskTags.isPending || createTag.isPending}
            className="max-w-full"
          />
        ) : (
          selectedTagOptions.length > 0 && (
            <Flex gap="xs" wrap>
              {selectedTagOptions.map((tag) => (
                <TagBadge key={tag.id} name={tag.name} color={tag.color} size="sm" />
              ))}
            </Flex>
          )
        )}

        {/* Relevant Customers */}
        {(task.relevantCustomers?.length > 0 || canEdit) && (
          <Flex gap="xs" wrap align="center">
            <Text size="sm" className="text-muted-foreground">
              Customers
            </Text>
            {task.relevantCustomers?.map((c) => (
              <CustomerBadge key={c.id} taskId={task.id} customer={c} canEdit={canEdit} />
            ))}
            {canEdit && <AddCustomerButton taskId={task.id} />}
          </Flex>
        )}
      </DrawerHeader>

      <DrawerContent className="space-y-7">
        {/* Description — clean inline body text, double-click to edit */}
        {(task.description || canEdit) && (
          <InlineEditableText
            value={task.description ?? ''}
            canEdit={canEdit}
            multiline
            onSave={(description) => updateTask.mutate({ id: task.id, description })}
            ariaLabel="Task description"
            placeholder="Add a description…"
            className="text-[15px] leading-relaxed text-foreground"
            renderDisplay={(value) => (
              <Markdown
                compact
                className="text-[15px] leading-relaxed [&_img]:max-w-full [&_img]:rounded-md [&_img]:my-2"
              >
                {value}
              </Markdown>
            )}
          />
        )}

        {/* Subtasks (only for potential parent tasks) */}
        {!task.parentTaskId && (
          <section className="space-y-3 border-t border-border pt-6">
            <Flex align="center" justify="between">
              <SectionLabel>
                Subtasks
                {task.subtaskSummary && task.subtaskSummary.total > 0
                  ? ` · ${task.subtaskSummary.completed}/${task.subtaskSummary.total}`
                  : ''}
              </SectionLabel>
              {canEdit && <CreateSubtaskButton parentTask={task} />}
            </Flex>
            {task.subtaskSummary && task.subtaskSummary.total > 0 && (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{
                    width: `${(task.subtaskSummary.completed / task.subtaskSummary.total) * 100}%`,
                  }}
                />
              </div>
            )}
            {(subtasksQuery.data?.data?.length ?? 0) === 0 && (
              <Text size="sm" className="text-muted-foreground">
                No subtasks yet.
              </Text>
            )}
            {subtasksQuery.data?.data.map((sub: AgentTask) => {
              const isCompleted = sub.status === 'completed';
              return (
                <button
                  key={sub.id}
                  className={`w-full rounded-md border border-border bg-card p-2.5 text-left shadow-sm transition-shadow hover:shadow-md ${isCompleted ? 'opacity-60' : ''}`}
                  onClick={() =>
                    void navigate({
                      to: '/tasks',
                      search: { ...search, taskId: sub.id },
                    })
                  }
                >
                  <Flex align="center" gap="sm">
                    <TagBadge
                      name={sub.status.replace('_', ' ')}
                      color={
                        badgeVariantToTagColor(subtaskStatusVariant(sub.status))
                      }
                      hideDot
                      size="sm"
                    />
                    <Text
                      size="sm"
                      className={`flex-1 truncate ${isCompleted ? 'line-through text-muted-foreground' : ''}`}
                    >
                      {sub.title}
                    </Text>
                    <Text size="xs" className="text-muted-foreground font-mono">
                      {sub.id.slice(0, 8)}
                    </Text>
                  </Flex>
                </button>
              );
            })}
          </section>
        )}

        {/* Comments */}
        <section className="space-y-3 border-t border-border pt-6">
          <SectionLabel>Comments</SectionLabel>
          <CommentsTab
            comments={commentsQuery.data?.data ?? []}
            pagination={commentsQuery.data?.pagination}
            page={commentPage}
            isLoading={commentsQuery.isLoading}
            isError={commentsQuery.isError}
            isFetching={commentsQuery.isFetching}
            commentText={commentText}
            isPosting={createComment.isPending}
            onCommentTextChange={setCommentText}
            onAddComment={() => {
              if (!commentText.trim()) return;
              createComment.mutate(
                { taskId: task.id, body: commentText.trim() },
                { onSuccess: () => setCommentText('') }
              );
            }}
            onPageChange={setCommentPage}
            className="space-y-4"
            mentionableMembers={members}
          />
        </section>

        {/* Event timeline */}
        {task.events && task.events.length > 0 && (
          <section className="border-t border-border pt-6">
            <SectionLabel>Activity</SectionLabel>
            <div className="relative ml-3 mt-3">
              {/* Vertical line */}
              <div className="absolute left-0 top-2 bottom-2 w-px bg-border" />
              <div className="space-y-4">
                {task.events.map((event) => (
                  <div key={event.id} className="relative pl-6">
                    {/* Dot */}
                    <div className="absolute left-[-3px] top-[7px] w-[7px] h-[7px] rounded-full bg-muted-foreground/50 ring-2 ring-background" />
                    <Text size="sm">
                      <span className="font-medium">{event.eventType.replace(/_/g, ' ')}</span>
                      <span className="text-muted-foreground">
                        {' '}
                        · {formatDistanceToNow(new Date(event.createdAt))} ago by{' '}
                        {resolveActorName(event.actor)}
                      </span>
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </DrawerContent>

      <TaskDrawerFooter task={task} onDeleted={handleDeleted} />
    </Drawer>
  );
}
